import { useState } from "react";

import { api, clinicHeader, readError } from "../api/client";
import { readableTime, recordSummary, type ClinicalRecord, type RecordType } from "../types";
import { DocumentRow } from "./DocumentRow";
import { ParseReview } from "./ParseReview";
import { RecordForm } from "./RecordForm";
import { ErrorNote } from "./ErrorNote";

/**
 * One group of records, and the two things a doctor does to them.
 *
 * Release decides whether the patient sees a record (CLN-F-021). Unreleasing
 * takes back something the patient may already have read, so it asks for a
 * reason before it happens.
 *
 * A restricted record is marked. It is not hidden here, because a record this
 * table received is a record this caller may see: the filter ran in the
 * database query, not in this component.
 */
export function RecordTable({
  title,
  records,
  canRelease,
  role,
  onChanged,
}: {
  title: string;
  records: ClinicalRecord[];
  canRelease: boolean;
  role: string | undefined;
  onChanged: () => void;
}) {
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [correcting, setCorrecting] = useState<string | null>(null);
  /**
   * Which document is being reviewed, held here rather than in the row.
   *
   * The review used to open inside the first cell, which is one column of a
   * five-column table: a screen with a monospace line from a lab report and
   * two fields beside each other had about a third of the page to do it in.
   * It opens as its own full-width row now, the same way a correction does.
   */
  const [reviewing, setReviewing] = useState<string | null>(null);

  async function toggleRelease(record: ClinicalRecord) {
    if (!record.id || !record.type) return;

    let reason = "";
    if (record.released) {
      const typed = window.prompt(
        "The patient can see this record. Why are you taking it back?",
      );
      if (typed === null) return;
      reason = typed.trim();
      if (!reason) {
        setError("Taking a record back needs a reason.");
        return;
      }
    }

    setBusyId(record.id);
    setError(null);

    const { error: failed } = await api.POST("/portal/records/{id}/release", {
      params: { path: { id: record.id }, header: clinicHeader() },
      body: { type: record.type as RecordType, released: !record.released, reason },
    });
    setBusyId(null);

    if (failed) {
      setError(readError(failed).message);
      return;
    }
    onChanged();
  }

  /**
   * Mark a record as never having been valid (decision C2).
   *
   * Different from a correction. A correction says the record was about the
   * right patient and the detail was wrong. This says the row should not be on
   * this chart at all, which is why it leaves the patient's view entirely.
   * Nothing is deleted either way.
   */
  async function markInError(record: ClinicalRecord) {
    if (!record.id || !record.type) return;

    const confirmed = window.confirm(
      "Mark this as recorded in error? It leaves the patient's view and stays in the " +
        "chart history. Use a correction instead if the record belongs here and a " +
        "detail was wrong.",
    );
    if (!confirmed) return;

    setBusyId(record.id);
    setError(null);

    const { error: failed } = await api.POST("/portal/records/{id}/supersede", {
      params: { path: { id: record.id }, header: clinicHeader() },
      body: { type: record.type as RecordType, recorded_in_error: true },
    });
    setBusyId(null);

    if (failed) {
      setError(readError(failed).message);
      return;
    }
    onChanged();
  }

  return (
    <div className="group">
      <h2>{title}</h2>
      <ErrorNote message={error} />

      {/* Its own scroll box, so a wide chart never scrolls the whole page. */}
      <div className="table-scroll">
        <table>
        <thead>
          <tr>
            <th>Record</th>
            <th>Recorded by</th>
            {/* "When" was ambiguous between two real dates. The portal shows
                when a measurement was written down; the patient app shows when
                it was taken. Both are right for their reader, and a doctor
                comparing the two screens has to be able to see which is which
                (ADR-017). */}
            <th>Recorded</th>
            <th>Patient sees it</th>
            {canRelease && <th />}
          </tr>
        </thead>
        <tbody>
          {records.map((record) => [
            <tr key={record.id}>
              <td>
                {recordSummary(record)}
                {record.sensitivity_level === "restricted" && (
                  <span className="tag restricted">Restricted</span>
                )}
                {record.type === "document" && (
                  <DocumentRow
                    record={record}
                    reviewing={reviewing === record.id}
                    onToggleReview={() =>
                      setReviewing(reviewing === record.id ? null : (record.id ?? null))
                    }
                  />
                )}
              </td>
              <td>{record.recorded_by_name || "—"}</td>
              <td className="muted">{readableTime(record.recorded_at)}</td>
              <td>{record.released ? "Yes" : "Not yet"}</td>
              {canRelease && (
                <td className="row-actions">
                  <button
                    type="button"
                    className="link"
                    disabled={busyId === record.id}
                    onClick={() => void toggleRelease(record)}
                  >
                    {record.released ? "Take back" : "Release"}
                  </button>

                  {record.type !== "document" && (
                    <>
                      <button
                        type="button"
                        className="link"
                        onClick={() =>
                          setCorrecting(correcting === record.id ? null : (record.id ?? null))
                        }
                      >
                        {correcting === record.id ? "Cancel" : "Correct"}
                      </button>
                      <button
                        type="button"
                        className="link danger"
                        disabled={busyId === record.id}
                        onClick={() => void markInError(record)}
                      >
                        Recorded in error
                      </button>
                    </>
                  )}
                </td>
              )}
            </tr>,
            reviewing === record.id && record.id ? (
              <tr key={`${record.id}-review`}>
                <td colSpan={canRelease ? 5 : 4}>
                  <ParseReview documentId={record.id} onChanged={onChanged} />
                </td>
              </tr>
            ) : null,
            correcting === record.id ? (
              <tr key={`${record.id}-correction`}>
                <td colSpan={canRelease ? 5 : 4}>
                  <RecordForm
                    mode={{
                      kind: "correct",
                      recordId: record.id ?? "",
                      type: record.type as RecordType,
                      payload: (record.payload ?? {}) as Record<string, unknown>,
                    }}
                    role={role}
                    onDone={() => {
                      setCorrecting(null);
                      onChanged();
                    }}
                    onCancel={() => setCorrecting(null)}
                  />
                </td>
              </tr>
            ) : null,
          ])}
        </tbody>
        </table>
      </div>
    </div>
  );
}
